import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { io, type Socket } from 'socket.io-client';
import { ChevronLeft, Copy, Crown, Globe2, Lock, LogOut, RefreshCw, UserRound, Users } from 'lucide-react';
import {
  fetchRoom,
  fetchVideos,
  getSocketUrl,
  getStreamUrl,
  joinRoom,
  leaveRoom,
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { VideoPlayer } from '../components/VideoPlayer';
import { UserAvatar } from '../components/UserAvatar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import type { RemotePlaybackCommand, Room, Video } from '../types';

interface SocketAck {
  ok: boolean;
  room?: Room;
  presence?: RoomPresencePayload;
  error?: {
    message: string;
  };
}

interface PlaybackPayload {
  currentTime?: number;
}

interface ChangeVideoPayload {
  room?: Room;
}

interface RoomPresencePayload {
  roomId: string;
  activeParticipantCount: number;
  activeParticipants: NonNullable<Room['activeParticipants']>;
}

interface RoomOwnerPayload {
  roomId: string;
  ownerId: string;
}

interface RoomDeletedPayload {
  roomId: string;
}

type PlaybackEventName = 'play' | 'pause' | 'seek';

function ackMessage(ack: SocketAck): string {
  return ack.error?.message || 'Socket action failed.';
}

function videoOptionLabel(video: Video): string {
  return video.visibility === 'private' ? `${video.title} (Private)` : video.title;
}

function roomInviteUrl(room: Room): string {
  return `${window.location.origin}${room.invitePath || `/rooms/${room.id}?invite=1`}`;
}

export const RoomWatchPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, logout, user } = useAuth();
  const isInviteLink = new URLSearchParams(location.search).get('invite') === '1';

  const socketRef = useRef<Socket | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [remoteCommand, setRemoteCommand] = useState<RemotePlaybackCommand | null>(null);
  const [loading, setLoading] = useState(true);
  const [socketState, setSocketState] = useState('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [changingVideo, setChangingVideo] = useState(false);

  const readyVideos = useMemo(
    () => videos.filter((video) => video.status === 'ready'),
    [videos]
  );

  const pushRemoteCommand = useCallback((type: PlaybackEventName, currentTime: number) => {
    setRemoteCommand((previous) => ({
      type,
      currentTime,
      sequence: (previous?.sequence ?? 0) + 1,
    }));
  }, []);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      let roomData: Room;
      try {
        roomData = await fetchRoom(id);
      } catch (err) {
        if (!isInviteLink) throw err;
        roomData = await joinRoom(id);
        navigate(`/rooms/${id}`, { replace: true });
      }
      const videoData = await fetchVideos();
      setRoom(roomData);
      setVideos(videoData);
      setSelectedVideoId(roomData.currentVideoId);
      setError(null);
    } catch {
      setError(isInviteLink ? 'Unable to join this invite.' : 'Unable to load this room.');
    } finally {
      setLoading(false);
    }
  }, [id, isInviteLink, navigate]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!id || !token) return;

    const socket = io(getSocketUrl(), {
      auth: { token },
    });
    socketRef.current = socket;
    setSocketState('connecting');

    const applyPlayback = (type: PlaybackEventName, payload: PlaybackPayload) => {
      const currentTime = Number(payload.currentTime ?? 0);
      if (!Number.isFinite(currentTime)) return;
      pushRemoteCommand(type, currentTime);
      setRoom((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          playback: {
            ...previous.playback,
            isPlaying: type === 'play'
              ? true
              : type === 'pause'
                ? false
                : previous.playback.isPlaying,
            currentTime,
            updatedAt: new Date().toISOString(),
          },
        };
      });
    };

    socket.on('connect', () => {
      setSocketState('joining');
      socket.emit('room:join', { roomId: id }, (ack: SocketAck) => {
        if (ack.ok && ack.room) {
          setSocketState('connected');
          setRoom(ack.room);
          setSelectedVideoId(ack.room.currentVideoId);
          setError(null);
        } else {
          setSocketState('error');
          setError(ackMessage(ack));
        }
      });
    });

    socket.on('connect_error', (err) => {
      setSocketState('error');
      setError(err.message || 'Socket connection failed.');
    });

    socket.on('disconnect', () => {
      setSocketState('disconnected');
    });

    socket.on('room:presence', (payload: RoomPresencePayload) => {
      if (payload.roomId !== id) return;
      setRoom((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          activeParticipantCount: payload.activeParticipantCount,
          activeParticipants: payload.activeParticipants,
        };
      });
    });

    socket.on('room:owner_changed', (payload: RoomOwnerPayload) => {
      if (payload.roomId !== id) return;
      setRoom((previous) => previous
        ? { ...previous, ownerId: payload.ownerId, isOwner: user?.id === payload.ownerId }
        : previous
      );
    });

    socket.on('room:deleted', (payload: RoomDeletedPayload) => {
      if (payload.roomId !== id) return;
      navigate('/rooms');
    });

    socket.on('play', (payload: PlaybackPayload) => applyPlayback('play', payload));
    socket.on('pause', (payload: PlaybackPayload) => applyPlayback('pause', payload));
    socket.on('seek', (payload: PlaybackPayload) => applyPlayback('seek', payload));
    socket.on('change_video', (payload: ChangeVideoPayload) => {
      if (payload.room) {
        setRoom(payload.room);
        setSelectedVideoId(payload.room.currentVideoId);
        setRemoteCommand(null);
      }
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [id, navigate, token, pushRemoteCommand, user?.id]);

  const emitPlayback = useCallback((eventName: PlaybackEventName, currentTime: number) => {
    const socket = socketRef.current;
    if (!socket?.connected || !id) return;

    socket.emit(eventName, { roomId: id, currentTime }, (ack: SocketAck) => {
      if (!ack.ok) setError(ackMessage(ack));
    });
  }, [id]);

  const handleChangeVideo = () => {
    const socket = socketRef.current;
    if (!room?.isOwner) {
      setError('Only the room admin can change video.');
      return;
    }
    if (!socket?.connected || !id || !selectedVideoId) return;

    setChangingVideo(true);
    setError(null);
    socket.emit('change_video', { roomId: id, videoId: selectedVideoId }, (ack: SocketAck) => {
      setChangingVideo(false);
      if (ack.ok && ack.room) {
        setRoom(ack.room);
        setSelectedVideoId(ack.room.currentVideoId);
        setRemoteCommand(null);
      } else {
        setError(ackMessage(ack));
      }
    });
  };

  const handleLeaveRoom = async () => {
    if (!id) return;
    try {
      await leaveRoom(id);
      navigate('/rooms');
    } catch {
      setError('Unable to leave this room.');
    }
  };

  const handleBackToRooms = async () => {
    if (!id) {
      navigate('/rooms');
      return;
    }
    try {
      await leaveRoom(id);
    } catch {
      // The room may already be deleted by inactive cleanup or another tab.
    } finally {
      navigate('/rooms');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-t-transparent border-foreground rounded-full animate-spin" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 space-y-4 text-center">
            <p className="font-black text-destructive">{error || 'Room not found.'}</p>
            <Button onClick={() => navigate('/rooms')}>Back to rooms</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const video = room.currentVideo;
  const streamUrl = video?.streamUrl ? getStreamUrl(video.streamUrl, video.visibility === 'private') : null;
  const inviteLink = roomInviteUrl(room);
  const adminParticipant = room.activeParticipants?.find((participant) => participant.id === room.ownerId);
  const adminName = adminParticipant?.name || adminParticipant?.login || (room.isOwner ? user?.name || user?.login : null);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b-2 border-foreground bg-primary text-primary-foreground">
        <div className="mx-auto flex min-h-14 max-w-6xl flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={() => void handleBackToRooms()} className="text-primary-foreground hover:text-primary-foreground">
              <ChevronLeft className="w-4 h-4" />
              Rooms
            </Button>
            <div className="hidden h-5 w-0.5 bg-primary-foreground sm:block" />
            <div className="min-w-0">
              <h1 className="font-black text-primary-foreground truncate">{room.name}</h1>
              <p className="text-xs font-mono text-primary-foreground/70">Socket: {socketState}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => void loadData()} className="text-primary-foreground hover:text-primary-foreground">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/account')} className="text-primary-foreground hover:text-primary-foreground">
              <UserRound className="w-4 h-4" />
              Account
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-primary-foreground hover:text-primary-foreground">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-2 py-4 sm:px-6 sm:py-6">
        {error && (
          <p className="border-2 border-destructive bg-destructive text-destructive-foreground px-3 py-2 text-sm font-bold">
            {error}
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_320px] xl:gap-5">
          <section className="space-y-3 min-w-0">
            {video?.status === 'ready' && streamUrl ? (
              <VideoPlayer
                key={video.id}
                src={streamUrl}
                title={video.title}
                videoId={video.id}
                remoteCommand={remoteCommand}
                onLocalPlay={(currentTime) => emitPlayback('play', currentTime)}
                onLocalPause={(currentTime) => emitPlayback('pause', currentTime)}
                onLocalSeek={(currentTime) => emitPlayback('seek', currentTime)}
                enablePlaybackResume={false}
              />
            ) : (
              <Card>
                <CardContent className="p-8 text-center font-bold text-foreground/60">
                  The room video is not ready for playback.
                </CardContent>
              </Card>
            )}
          </section>

          <aside className="space-y-4">
            <Card>
              <CardContent className="space-y-3 p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <h2 className="font-black">Session</h2>
                </div>
                <div className="text-xs font-mono font-black border-2 border-foreground p-2 bg-primary/20 break-all">
                  Room ID: {room.id}
                </div>
                <div className="flex items-center gap-2 text-xs font-mono font-black border-2 border-foreground p-2 bg-card">
                  {room.visibility === 'private' ? <Lock className="w-3 h-3" /> : <Globe2 className="w-3 h-3" />}
                  {room.visibility === 'private' ? 'Private room' : 'Public room'}
                </div>
                <div className="flex items-center justify-between gap-2 border-2 border-foreground bg-card p-2 text-xs font-mono font-black">
                  <span className="inline-flex items-center gap-1">
                    <Crown className="h-3.5 w-3.5" />
                    Admin
                  </span>
                  <span className="min-w-0 truncate text-right">
                    {adminName || 'Not active'}
                  </span>
                </div>
                {room.isOwner && (
                  <div className="border-2 border-foreground bg-primary px-2 py-1.5 text-xs font-mono font-black text-primary-foreground">
                    You are room admin
                  </div>
                )}
                <div className="space-y-2">
                  <div className="text-xs font-mono font-black border-2 border-foreground p-2 bg-card break-all">
                    {inviteLink}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => void navigator.clipboard.writeText(inviteLink)}
                  >
                    <Copy className="w-4 h-4" />
                    Copy invite link
                  </Button>
                </div>
                <div className="text-sm font-bold">
                  <div className="border-2 border-foreground bg-card p-2">
                    <span className="block text-[10px] uppercase text-foreground/50">Active</span>
                    {room.activeParticipantCount ?? room.activeParticipants?.length ?? 0}
                  </div>
                </div>
                <div className="space-y-2">
                  {(room.activeParticipants || []).length > 0 ? (
                    room.activeParticipants?.map((participant) => {
                      const isAdmin = participant.id === room.ownerId;
                      return (
                        <div key={participant.id} className="flex items-center gap-2 border-2 border-foreground bg-card px-2 py-1.5">
                          <UserAvatar person={participant} size="sm" />
                          <span className="min-w-0 flex-1 truncate text-sm font-bold">
                            {participant.name || participant.login}
                          </span>
                          {isAdmin && (
                            <span className="inline-flex shrink-0 items-center gap-1 border-2 border-foreground bg-primary px-1.5 py-0.5 text-[10px] font-mono font-black uppercase text-primary-foreground shadow-neo-sm">
                              <Crown className="h-3 w-3" />
                              Admin
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="border-2 border-foreground bg-card px-2 py-3 text-sm font-bold text-foreground/60">
                      No active tabs in this room.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-3 sm:p-4">
                <h2 className="font-black">Change video</h2>
                {room.isOwner ? (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="room-video-select">Ready video</Label>
                      <select
                        id="room-video-select"
                        value={selectedVideoId}
                        onChange={(event) => setSelectedVideoId(event.target.value)}
                        className="w-full h-10 border-2 border-foreground bg-background px-3 text-sm font-bold text-foreground shadow-neo-sm"
                      >
                        {readyVideos.map((readyVideo) => (
                          <option key={readyVideo.id} value={readyVideo.id}>{videoOptionLabel(readyVideo)}</option>
                        ))}
                      </select>
                    </div>
                    <Button
                      type="button"
                      className="w-full"
                      disabled={changingVideo || !selectedVideoId || selectedVideoId === room.currentVideoId}
                      onClick={handleChangeVideo}
                    >
                      Change video
                    </Button>
                  </>
                ) : (
                  <div
                    className="flex items-center gap-2 border-2 border-foreground bg-muted p-3 text-sm font-bold text-foreground/70"
                    role="status"
                  >
                    <Lock className="h-4 w-4 shrink-0" />
                    Only the room admin can change video.
                  </div>
                )}
              </CardContent>
            </Card>

            <Button type="button" variant="outline" className="w-full" onClick={() => void handleLeaveRoom()}>
              Leave room
            </Button>
          </aside>
        </div>
      </main>
    </div>
  );
};
